/* RUFF Editorial — product page behavior.
   Option pills → variant resolution, price/button/SKU updates, variant media
   scroll, ?variant= URL sync, and quantity stepper. Degrades gracefully:
   without JS the theme shows a plain variant <select> inside the form. */
(function () {
  'use strict';

  var PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (isNaN(cents) || cents == null) return '0';
    var fixed = (cents / 100).toFixed(precision == null ? 2 : precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  function formatMoney(cents, format) {
    var pattern = format || (window.ruffTheme && window.ruffTheme.moneyFormat) || '${{amount}}';
    var match = pattern.match(PLACEHOLDER);
    var value;
    var decimal = '.';
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        decimal = ',';
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    // Mirrors Liquid's money_without_trailing_zeros, which the templates use.
    // Without this the price is whole-dollar on load and then grows a ".00"
    // the moment a variant is picked and this function re-renders it. Only a
    // zero-cent amount loses its decimals — $99.50 keeps them.
    if (cents % 100 === 0) {
      value = value.replace(new RegExp('\\' + decimal + '00$'), '');
    }
    return pattern.replace(PLACEHOLDER, value);
  }

  function themeStrings() {
    return (window.ruffTheme && window.ruffTheme.strings) || {};
  }

  class RuffProduct extends HTMLElement {
    connectedCallback() {
      var dataNode = this.querySelector('[data-variant-json]');
      try {
        this.variants = dataNode ? JSON.parse(dataNode.textContent) : [];
      } catch (error) {
        this.variants = [];
      }
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      var minus = event.target.closest('[data-quantity-minus]');
      var plus = event.target.closest('[data-quantity-plus]');
      if (!minus && !plus) return;
      var stepper = (minus || plus).closest('.quantity-stepper');
      var input = stepper && stepper.querySelector('input[name="quantity"]');
      if (!input) return;
      var min = Number(input.min) || 1;
      var next = (Number(input.value) || min) + (plus ? 1 : -1);
      if (input.max) next = Math.min(next, Number(input.max));
      input.value = Math.max(min, next);
    }

    onChange(event) {
      if (event.target.matches('input[name="quantity"]')) {
        var min = Number(event.target.min) || 1;
        if (!event.target.value || Number(event.target.value) < min) event.target.value = min;
        return;
      }
      if (!event.target.matches('[data-option-value]')) return;
      var selected = this.selectedOptions();
      var variant = this.variants.find(function (candidate) {
        var options = candidate.options || [];
        return (
          options.length === selected.length &&
          options.every(function (value, index) {
            return value === selected[index];
          })
        );
      });
      this.currentVariant = variant || null;
      this.updateOptionLabels(selected);
      this.updateOptionAvailability(selected);
      this.updateVariantInput(variant);
      this.updateButton(variant);
      this.updatePrice(variant);
      this.updateSku(variant);
      this.updateMedia(variant);
      this.updateUrl(variant);
    }

    // Marks option values that have no in-stock variant alongside the current
    // selection. Liquid does this on load; without repeating it here the
    // strikethroughs would describe the colour the shopper started on rather
    // than the one they are looking at.
    updateOptionAvailability(selected) {
      var self = this;
      this.querySelectorAll('fieldset[data-option-index]').forEach(function (fieldset) {
        var index = Number(fieldset.dataset.optionIndex);
        fieldset.querySelectorAll('input[data-option-value]').forEach(function (input) {
          var available = self.variants.some(function (candidate) {
            if (!candidate.available) return false;
            var options = candidate.options || [];
            if (options[index] !== input.value) return false;
            // Every other option has to match what is currently chosen; this
            // one is the value being tested.
            return selected.every(function (value, i) {
              return i === index || value == null || options[i] === value;
            });
          });
          var label = fieldset.querySelector('label[for="' + input.id + '"]');
          if (label) label.classList.toggle('variant-pill--unavailable', !available);
        });
      });
    }

    // Keeps the value echoed under each option label in step with the
    // radios. Driven off `selected`, not the matched variant, so it still
    // reads correctly for a combination that has no variant behind it.
    updateOptionLabels(selected) {
      var self = this;
      selected.forEach(function (value, index) {
        var label = self.querySelector('[data-option-current="' + index + '"]');
        if (label) label.textContent = value || '';
      });
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('fieldset[data-option-index]')).map(function (fieldset) {
        var checked = fieldset.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    }

    updateVariantInput(variant) {
      var select = this.querySelector('[data-variant-select]');
      if (select && variant) select.value = String(variant.id);
    }

    updateButton(variant) {
      var button = this.querySelector('[data-product-submit]');
      if (!button) return;
      var label = button.querySelector('[data-submit-label]') || button;
      var strings = themeStrings();
      if (!variant) {
        button.disabled = true;
        label.textContent = strings.unavailable || button.dataset.unavailableLabel || '';
      } else if (!variant.available) {
        button.disabled = true;
        label.textContent = strings.soldOut || button.dataset.soldLabel || '';
      } else {
        button.disabled = false;
        label.textContent = strings.addToCart || button.dataset.addLabel || '';
      }
    }

    updatePrice(variant) {
      var target = this.querySelector('[data-price]');
      if (!target || !variant) return;
      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      var html = '<span class="price' + (onSale ? ' price--on-sale' : '') + '">';
      if (onSale) {
        html +=
          '<span class="visually-hidden">' + escapeHtml(target.dataset.saleLabel) + '</span>' +
          '<span class="price__sale">' + escapeHtml(formatMoney(variant.price)) + '</span>' +
          '<span class="visually-hidden">' + escapeHtml(target.dataset.regularLabel) + '</span>' +
          '<s class="price__compare">' + escapeHtml(formatMoney(variant.compare_at_price)) + '</s>';
      } else {
        html += escapeHtml(formatMoney(variant.price));
      }
      if (variant.unit_price != null && variant.unit_price_measurement) {
        var reference =
          (variant.unit_price_measurement.reference_value || 1) +
          (variant.unit_price_measurement.reference_unit || '');
        html += '<span class="price__unit">' + escapeHtml(formatMoney(variant.unit_price) + ' / ' + reference) + '</span>';
      }
      html += '</span>';
      target.innerHTML = html;
    }

    updateSku(variant) {
      var target = this.querySelector('[data-sku]');
      if (!target) return;
      var sku = variant && variant.sku ? variant.sku : '';
      target.textContent = sku;
      var wrapper = this.querySelector('[data-sku-wrapper]');
      if (wrapper) wrapper.classList.toggle('is-hidden', sku === '');
    }

    updateMedia(variant) {
      if (!variant) return;
      var media = variant.featured_media || variant.featured_image;
      if (!media || media.id == null) return;
      var target = this.querySelector('[data-media-id="' + String(media.id).replace(/"/g, '') + '"]');
      if (!target) return;
      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest' });
    }

    updateUrl(variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  if (!customElements.get('ruff-product')) customElements.define('ruff-product', RuffProduct);
})();
